import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import _ from 'lodash'
import { Pencil, Trash2 } from 'lucide-react'
import { observer } from 'mobx-react'
import { useRef, useState } from 'react'
import { useParams } from 'react-router'

import { ConfirmButton } from '@/components/ConfirmButton'
import { DataTable } from '@/components/DataTable'
import { SortableColumnHeader } from '@/components/DataTable/DataTable'
import { Button } from '@/components/elements/button'
import { Input } from '@/components/elements/input'
import { Layout } from '@/components/Layout'
import AddEditJobSlideout from '@/pages/Accounts/Jobs/AddEditJobSlideout'
import { deleteOrganizationJob, getOrganizationJobs } from '@/services/organization.service'
import { toast } from '@/utils/helpers'

const OrganizationJobs = observer(() => {
  const { organizationId } = useParams()

  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [sorting, setSorting] = useState([{ id: 'title', desc: false }])
  const [pagination, setPagination] = useState({ pageSize: 20, pageIndex: 0 })
  const [showSlideout, setShowSlideout] = useState(false)
  const [editJob, setEditJob] = useState(null)

  const queryClient = useQueryClient()
  const queryKey = ['organization-jobs', organizationId, sorting, pagination, searchTerm]

  const { data: jobs, isPending } = useQuery({
    queryKey,
    queryFn: () => getOrganizationJobs({ organizationId, sorting, pagination, searchTerm }),
    placeholderData: keepPreviousData,
    enabled: !!organizationId,
  })

  const { isPending: pendingDelete, mutate: deleteJob } = useMutation({
    mutationFn: (id) => deleteOrganizationJob({ organizationId, id }),
    onError: (err) => toast(err.message, 'error'),
    onSuccess: () => {
      toast('Job deleted successfully', 'success')
      queryClient.invalidateQueries({ queryKey: ['organization-jobs', organizationId] })
    },
  })

  const resetToFirstPage = () =>
    setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }))

  const debounceSearch = useRef(
    _.debounce((value) => {
      setSearchTerm(value)
      resetToFirstPage()
    }, 500),
  ).current

  const openEdit = (job) => {
    setEditJob(job)
    setShowSlideout(true)
  }

  return (
    <Layout breadcrumbs={['Organization', 'Jobs']}>
      <Layout.Heading title="Jobs" />
      <p className="text-muted-foreground mb-3 max-w-3xl text-sm">
        Job titles defined here are shared with every facility in the organization. Facilities can
        assign them to members and EAP questions but can&apos;t edit them — your changes propagate.
      </p>

      <div className="flex w-full flex-row items-center justify-between">
        <Input
          className="w-full md:w-64"
          placeholder="Search jobs"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            debounceSearch(e.target.value)
          }}
        />
        <Button onClick={() => setShowSlideout(true)}>Add Job</Button>
      </div>

      <div className="mt-3">
        <DataTable
          columns={[
            {
              accessorKey: 'title',
              id: 'title',
              header: ({ column }) => <SortableColumnHeader column={column} label="Title" />,
              cell: ({ row }) => (
                <button
                  type="button"
                  className="text-action cursor-pointer text-left hover:underline"
                  onClick={() => openEdit(row.original)}
                >
                  {row.original.title}
                </button>
              ),
            },
            {
              accessorKey: 'description',
              header: 'Description',
              cell: ({ row }) => (
                <span className="line-clamp-2">{row.original.description || ''}</span>
              ),
            },
            {
              id: 'edit',
              size: 80,
              cell: ({ row }) => (
                <Button
                  className="place-self-end"
                  variant="outline"
                  size="sm"
                  onClick={() => openEdit(row.original)}
                >
                  <Pencil className="mr-1 h-4 w-4" />
                  Edit
                </Button>
              ),
            },
            {
              id: 'delete',
              size: 80,
              cell: ({ row }) => (
                <ConfirmButton
                  confirmContent={`Delete "${row.original.title}"? It will be removed from every facility in the organization.`}
                  confirmTitle="Delete Job"
                  className="place-self-end"
                  variant="destructive"
                  size="sm"
                  loading={pendingDelete}
                  onClick={() => deleteJob(row.original.id)}
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="sr-only">Delete job</span>
                </ConfirmButton>
              ),
            },
          ]}
          data={jobs?.results || []}
          loading={isPending}
          pagination={pagination}
          rowCount={jobs?.count || 0}
          setPagination={setPagination}
          sorting={sorting}
          setSorting={setSorting}
        />
      </div>

      {showSlideout && (
        <AddEditJobSlideout
          editJob={editJob}
          organizationId={organizationId}
          queryKey={queryKey}
          resetAndClose={() => {
            setShowSlideout(false)
            setEditJob(null)
            queryClient.invalidateQueries({ queryKey: ['organization-jobs', organizationId] })
          }}
        />
      )}
    </Layout>
  )
})

export default OrganizationJobs
